/**
 * ConciergeWidget Component
 *
 * Floating chat widget for the FlowInCash AI financial concierge.
 * Architecture mirrors the Crispi sidecar pattern:
 * - Floating FAB bubble (bottom-right)
 * - Slide-up panel with message list, status banner, and input
 * - Mic button wired to browser SpeechRecognition (where available)
 * - Audio playback handled by the sidecar Edge TTS proxy
 * - Route context passed to sidecar on mount
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  TextField,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  AccountBalance as AdvisorIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useConcierge } from './useConcierge';
import { ConciergeMessage } from './ConciergeMessage';
import { ConciergeActions } from './ConciergeActions';
import type { ConciergeAction } from './useConcierge';

// ─── Sidecar URL helpers ──────────────────────────────────────────────────────

function getSidecarWsUrl(): string {
  const envUrl = (import.meta.env as Record<string, string>)['VITE_SIDECAR_WS_URL'];
  if (envUrl) return envUrl;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/sidecar/chat`;
}

function getAuthToken(): string {
  return localStorage.getItem('authToken') ?? '';
}

// ─── SpeechRecognition type shim ─────────────────────────────────────────────

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((ev: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognitionInstance;
    webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
  }
}

function getSpeechRecognition(): (new () => SpeechRecognitionInstance) | null {
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const ConciergeWidget: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isListening, setIsListening] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);

  const { status, messages, actions, sendMessage, connect, disconnect } = useConcierge({
    url: getSidecarWsUrl(),
    token: getAuthToken(),
    autoConnect: false,
  });

  // Connect on first open only — NOT on mount. The widget previously opened
  // a WebSocket the instant the app loaded for every visitor, whether or not
  // they ever clicked the chat bubble, and auto-reconnected forever if it
  // dropped. That constant background traffic eats into the app's rate
  // limits for no benefit (2026-07-22: found via live testing — a few
  // requests every ~10s, all day, per open tab). Connecting lazily on first
  // open means idle visitors cost nothing.
  const hasConnectedRef = useRef(false);
  useEffect(() => {
    if (isOpen && !hasConnectedRef.current) {
      hasConnectedRef.current = true;
      connect({ route: location.pathname });
    }
  }, [isOpen, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Idle shutoff (two independent safety nets) ──────────────────────────
  //
  // 1. Closed-panel grace disconnect: if the user explicitly closes the
  //    panel (clicks X), disconnect after a short 60s grace period — not
  //    instantly, so a quick re-open (misclick) doesn't force a reconnect.
  //
  // 2. Left-open inactivity timeout: the case #1 does NOT cover — a user
  //    opens the chat, then walks away / switches apps / closes their
  //    laptop lid WITHOUT clicking the X. The panel stays "open" in React
  //    state forever, so #1 never fires and the connection would otherwise
  //    stay live indefinitely. This second timer tracks the last time
  //    something actually happened (panel opened, message sent, message
  //    received) and force-disconnects + closes the panel after 5 minutes
  //    of silence, regardless of whether the user ever clicked close.
  const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes of no activity while open
  const lastActivityRef = useRef<number>(Date.now());
  const bumpActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (isOpen) bumpActivity();
  }, [isOpen, bumpActivity]);

  useEffect(() => {
    if (messages.length > 0) bumpActivity();
  }, [messages.length, bumpActivity]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const interval = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= IDLE_TIMEOUT_MS) {
        disconnect();
        hasConnectedRef.current = false; // allow reconnect if opened again
        setIsOpen(false); // close the panel so the UI matches reality
      }
    }, 30_000); // check every 30s — coarse is fine, this is a safety net not a timer
    return () => clearInterval(interval);
  }, [isOpen, disconnect]);

  // Disconnect after the panel has been closed for a short idle grace period
  // (60s) — not immediately, so quickly re-opening (e.g. a misclick) doesn't
  // force a full reconnect, but a visitor who closes the chat and leaves the
  // tab open all day doesn't keep holding a live connection indefinitely.
  const idleDisconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (isOpen) {
      // Panel re-opened before the idle timer fired — cancel the pending disconnect.
      if (idleDisconnectRef.current) {
        clearTimeout(idleDisconnectRef.current);
        idleDisconnectRef.current = null;
      }
      return;
    }
    if (hasConnectedRef.current) {
      idleDisconnectRef.current = setTimeout(() => {
        disconnect();
        hasConnectedRef.current = false; // allow reconnect on next open
        idleDisconnectRef.current = null;
      }, 60_000);
    }
    return () => {
      if (idleDisconnectRef.current) {
        clearTimeout(idleDisconnectRef.current);
        idleDisconnectRef.current = null;
      }
    };
  }, [isOpen, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Auto-scroll to latest message
  // Guard: jsdom / older browsers may not implement scrollIntoView
  useEffect(() => {
    if (isOpen && typeof messagesEndRef.current?.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // ─── Send message ───────────────────────────────────────────────────────────

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    sendMessage(text);
    setInput('');
  }, [input, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ─── Mic / SpeechRecognition ────────────────────────────────────────────────

  const toggleMic = useCallback(() => {
    const SpeechRecognitionCtor = getSpeechRecognition();
    if (!SpeechRecognitionCtor) return;

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = Array.from(event.results)
        .map((r) => r[0].transcript)
        .join('');
      setInput((prev) => (prev ? `${prev} ${transcript}` : transcript));
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening]);

  // ─── Action handler ─────────────────────────────────────────────────────────

  const handleAction = useCallback(
    (id: string) => {
      sendMessage(`action:${id}`);
    },
    [sendMessage],
  );

  // ─── Status banner ──────────────────────────────────────────────────────────

  const renderStatusBanner = () => {
    if (status === 'connecting') {
      return (
        <Box sx={{ px: 2, py: 0.5, bgcolor: 'info.light' }}>
          <Typography variant="caption" color="info.contrastText">
            Connecting...
          </Typography>
        </Box>
      );
    }
    if (status === 'disconnected') {
      return (
        <Box sx={{ px: 2, py: 0.5, bgcolor: 'warning.light' }}>
          <Typography variant="caption" color="warning.contrastText">
            Reconnecting...
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // ─── Determine panel size ───────────────────────────────────────────────────

  const panelWidth = isMobile ? '100vw' : 360;
  const panelHeight = isMobile ? '100dvh' : 500;

  // ─── Floating actions from last assistant message ───────────────────────────
  // Show the most recent action set at the bottom of the message list
  const pendingActions: ConciergeAction[] = actions;

  return (
    <>
      {/* Floating bubble */}
      <Fab
        color="primary"
        onClick={() => setIsOpen((o) => !o)}
        aria-label="Open chat with your FlowInCash advisor"
        aria-expanded={isOpen}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1200,
          boxShadow: 4,
          transition: 'transform 0.2s ease-in-out',
          '&:hover': { transform: 'scale(1.05)' },
        }}
      >
        <ChatIcon />
      </Fab>

      {/* Chat panel — conditional render so tests can assert on presence/absence */}
      {isOpen && (
        <Paper
          role="dialog"
          aria-label="FlowInCash advisor chat"
          aria-modal="true"
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 0 : 88,
            right: isMobile ? 0 : 24,
            width: panelWidth,
            height: panelHeight,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1300,
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AdvisorIcon sx={{ fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={700}>
                FlowInCash Advisor
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={() => setIsOpen(false)}
              sx={{ color: 'inherit' }}
              aria-label="Close advisor panel"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Connection status banner */}
          {renderStatusBanner()}

          {/* Message list */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 2,
              py: 1.5,
              bgcolor: 'background.default',
            }}
          >
            {messages.length === 0 && status === 'connected' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AdvisorIcon sx={{ fontSize: 40, color: 'primary.light', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Hi! I&apos;m your FlowInCash advisor.
                  <br />
                  Ask me about your budget, spending, or a purchase.
                </Typography>
              </Box>
            )}

            {messages.map((msg) => (
              <ConciergeMessage
                key={msg.id}
                id={msg.id}
                role={msg.role}
                text={msg.text}
                timestamp={msg.timestamp}
                data={msg.data}
              />
            ))}

            {pendingActions.length > 0 && (
              <ConciergeActions actions={pendingActions} onAction={handleAction} />
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Input row */}
          <Box
            sx={{
              p: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <TextField
              fullWidth
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message to your advisor"
              size="small"
              autoComplete="off"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.default',
                },
              }}
            />

            <IconButton
              onClick={handleSend}
              disabled={!input.trim()}
              color="primary"
              aria-label="Send message"
              sx={{
                bgcolor: input.trim() ? 'primary.main' : 'action.disabledBackground',
                color: input.trim() ? 'primary.contrastText' : 'action.disabled',
                '&:hover': {
                  bgcolor: input.trim() ? 'primary.dark' : 'action.disabledBackground',
                },
                borderRadius: 2,
                p: 1,
                flexShrink: 0,
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>

            <IconButton
              onClick={toggleMic}
              color={isListening ? 'error' : 'default'}
              aria-label={isListening ? 'Stop microphone' : 'Use microphone'}
              sx={{ borderRadius: 2, p: 1, flexShrink: 0 }}
            >
              {isListening ? (
                <MicOffIcon fontSize="small" />
              ) : (
                <MicIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default ConciergeWidget;
